<template>
  <v-container fluid class="py-5 px-5 py-sm-10 px-sm-10 fill-height align-start">
    <v-row class="w-100">
      <!-- Page Header -->
      <v-col cols="12" class="d-flex flex-wrap align-center justify-space-between mb-2">
        <div class="text-start">
          <div class="textLight--text font-weight-bold text-h5 d-flex align-center">
            <v-icon color="primary" class="me-2" size="28">mdi-shield-search</v-icon>
            Login Audit & Security History
          </div>
          <div class="textLight--text text-caption font-weight-light mt-1">
            Real-time tracking of all successful and failed authentication attempts across the platform
          </div>
        </div>

        <div class="d-flex align-center flex-wrap mt-3 mt-sm-0">
          <v-btn
            depressed
            outlined
            color="primary"
            class="me-3 rounded-lg text-capitalize"
            @click="fetchAudits"
            :loading="loading"
          >
            <v-icon left size="18">mdi-refresh</v-icon>
            Refresh
          </v-btn>

          <v-btn
            depressed
            color="primary"
            class="rounded-lg text-capitalize"
            :to="{ name: 'ActiveSessions' }"
          >
            <v-icon left size="18">mdi-devices</v-icon>
            View Active Sessions
          </v-btn>
        </div>
      </v-col>

      <!-- Alert for Failed Attempts -->
      <v-col cols="12" v-if="stats.totalFailed > 0">
        <v-alert
          dense
          text
          type="error"
          icon="mdi-alert-octagon-outline"
          class="rounded-xl mb-0"
        >
          <div class="d-flex align-center justify-space-between flex-wrap">
            <span>
              <b>Security Notice:</b> <b>{{ stats.totalFailed }}</b> failed login attempt(s) recorded.
              Inspect the table below to investigate unauthorized IP addresses or brute-force attempts.
            </span>
            <v-btn
              small
              text
              color="error"
              class="font-weight-bold text-capitalize"
              @click="selectedStatus = 'FAILED'"
            >
              Filter Failed Only
            </v-btn>
          </div>
        </v-alert>
      </v-col>

      <!-- Overview Stats Cards -->
      <v-col cols="12" sm="6" md="3">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">Total Attempts</div>
              <div class="text-h4 font-weight-bold primary--text mt-1">{{ stats.totalAttempts }}</div>
            </div>
            <v-avatar color="primary lighten-5" size="48">
              <v-icon color="primary" size="26">mdi-history</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">Successful Logins</div>
              <div class="text-h4 font-weight-bold success--text mt-1">{{ stats.totalSuccess }}</div>
            </div>
            <v-avatar color="success lighten-5" size="48">
              <v-icon color="success" size="26">mdi-check-decagram</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">Failed Attempts</div>
              <div class="text-h4 font-weight-bold error--text mt-1">{{ stats.totalFailed }}</div>
            </div>
            <v-avatar color="error lighten-5" size="48">
              <v-icon color="error" size="26">mdi-alert-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card flat class="pa-4 rounded-xl border" :color="$vuetify.theme.dark ? '#1E1E1E' : '#F9FAFB'">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-caption textLight--text font-weight-medium">Unique Client IPs</div>
              <div class="text-h4 font-weight-bold deep-purple--text mt-1">{{ stats.uniqueIps }}</div>
            </div>
            <v-avatar color="deep-purple lighten-5" size="48">
              <v-icon color="deep-purple" size="26">mdi-ip-network-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <!-- Filters & Search Bar -->
      <v-col cols="12" class="d-flex flex-wrap align-center justify-space-between mt-2">
        <v-btn-toggle
          v-model="selectedStatus"
          mandatory
          dense
          color="primary"
          class="rounded-lg border mb-2 mb-sm-0"
        >
          <v-btn value="ALL" class="text-capitalize px-4">
            <v-icon left size="16">mdi-format-list-bulleted</v-icon>
            All ({{ stats.totalAttempts }})
          </v-btn>
          <v-btn value="FAILED" class="text-capitalize px-4" active-class="error--text">
            <v-icon left size="16" color="error">mdi-close-circle</v-icon>
            Failed Attempts ({{ stats.totalFailed }})
          </v-btn>
          <v-btn value="SUCCESS" class="text-capitalize px-4" active-class="success--text">
            <v-icon left size="16" color="success">mdi-check-circle</v-icon>
            Successful ({{ stats.totalSuccess }})
          </v-btn>
        </v-btn-toggle>

        <div style="max-width: 320px; width: 100%">
          <v-text-field
            v-model="searchQuery"
            dense
            outlined
            rounded
            hide-details
            placeholder="Search email, IP, city..."
            prepend-inner-icon="mdi-magnify"
            clearable
            @input="debounceSearch"
          ></v-text-field>
        </div>
      </v-col>

      <!-- Audit Log Table -->
      <v-col cols="12" class="mt-2">
        <v-card flat class="rounded-xl border">
          <v-data-table
            :headers="headers"
            :items="audits"
            :loading="loading"
            class="elevation-0"
            :items-per-page="itemsPerPage"
            hide-default-footer
            no-data-text="No login attempts found"
          >
            <!-- Status & Reason -->
            <template v-slot:item.status="{ item }">
              <div class="text-start py-2">
                <v-chip
                  small
                  label
                  :color="item.status === 'SUCCESS' ? 'success' : 'error'"
                  text-color="white"
                  class="font-weight-bold"
                >
                  <v-icon left x-small>
                    {{ item.status === 'SUCCESS' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                  {{ item.status }}
                </v-chip>
                <div v-if="item.failureReason" class="text-caption error--text font-weight-medium mt-1">
                  {{ item.failureReason }}
                </div>
              </div>
            </template>

            <!-- User / Attempted Email -->
            <template v-slot:item.user="{ item }">
              <div class="text-start">
                <div class="font-weight-medium text-body-2">
                  {{ item.userName || 'Unknown Account' }}
                </div>
                <div class="text-caption textLight--text font-monospace">
                  {{ item.attemptedEmail }}
                </div>
                <v-chip
                  v-if="item.userRole"
                  x-small
                  outlined
                  color="primary"
                  class="mt-1 font-weight-bold"
                >
                  {{ item.userRole }}
                </v-chip>
              </div>
            </template>

            <!-- IP & Location -->
            <template v-slot:item.location="{ item }">
              <div class="text-start">
                <div class="font-weight-medium text-body-2 d-flex align-center">
                  <v-icon size="14" class="me-1" color="grey">mdi-map-marker-outline</v-icon>
                  {{ formatLocation(item) }}
                </div>
                <div class="text-caption textLight--text font-monospace d-flex align-center mt-1">
                  {{ item.ipAddress && item.ipAddress !== 'Unknown' ? item.ipAddress : '-' }}
                  <v-tooltip bottom v-if="item.ipAddress && item.ipAddress !== 'Unknown'">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        v-bind="attrs"
                        v-on="on"
                        class="ms-1"
                        @click="copyIp(item.ipAddress)"
                      >
                        <v-icon size="12">mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                    <span>Copy IP</span>
                  </v-tooltip>
                </div>
              </div>
            </template>

            <!-- Device & Browser -->
            <template v-slot:item.device="{ item }">
              <div class="d-flex align-center text-start">
                <v-avatar :color="item.status === 'SUCCESS' ? 'primary lighten-5' : 'error lighten-5'" size="34" class="me-3">
                  <v-icon :color="item.status === 'SUCCESS' ? 'primary' : 'error'" size="18">
                    {{ getDeviceIcon(item.os) }}
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium text-body-2">
                    {{ item.browser && item.browser !== 'Unknown Browser' ? item.browser : 'Web Browser' }}
                  </div>
                  <div class="text-caption textLight--text font-weight-light">
                    {{ item.os && item.os !== 'Unknown OS' ? item.os : 'Desktop' }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Timestamp -->
            <template v-slot:item.createdAt="{ item }">
              <div class="text-start">
                <div class="text-body-2 font-weight-medium">{{ formatDate(item.createdAt) }}</div>
                <div class="text-caption textLight--text">{{ formatRelativeTime(item.createdAt) }}</div>
              </div>
            </template>
          </v-data-table>

          <!-- Pagination -->
          <v-divider v-if="totalPages > 1"></v-divider>
          <div class="d-flex justify-space-between align-center px-4 py-3" v-if="totalPages > 1">
            <div class="text-caption textLight--text">
              Showing page {{ currentPage }} of {{ totalPages }} ({{ totalCount }} records)
            </div>
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="7"
              dense
              circle
              @input="fetchAudits"
            ></v-pagination>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import { getLoginAudits } from "../methods/loginAuditMethods";

export default {
  name: "LoginAuditPage",
  data() {
    return {
      loading: false,
      audits: [],
      stats: {
        totalAttempts: 0,
        totalSuccess: 0,
        totalFailed: 0,
        uniqueIps: 0,
      },
      selectedStatus: "ALL",
      searchQuery: "",
      searchTimeout: null,
      currentPage: 1,
      itemsPerPage: 20,
      totalCount: 0,
      totalPages: 1,
      headers: [
        { text: "Status & Reason", value: "status", sortable: false, width: "160px" },
        { text: "Account / Attempted Email", value: "user", sortable: false },
        { text: "IP Address & Location", value: "location", sortable: false },
        { text: "Device & Browser", value: "device", sortable: false },
        { text: "Time & Date", value: "createdAt", sortable: false },
      ],
    };
  },
  watch: {
    selectedStatus() {
      this.currentPage = 1;
      this.fetchAudits();
    },
  },
  mounted() {
    this.fetchAudits();
  },
  methods: {
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchAudits();
      }, 400);
    },
    async fetchAudits() {
      this.loading = true;
      try {
        const res = await getLoginAudits({
          status: this.selectedStatus,
          search: this.searchQuery,
          page: this.currentPage,
          limit: this.itemsPerPage,
        });

        if (res.data) {
          this.audits = res.data.items || [];
          this.stats = res.data.stats || this.stats;
          this.totalCount = res.data.totalCount || 0;
          this.totalPages = res.data.totalPages || 1;
        }
      } catch (e) {
        console.error("Error fetching login audits:", e);
      } finally {
        this.loading = false;
      }
    },
    getDeviceIcon(os) {
      if (!os) return "mdi-laptop";
      const lower = os.toLowerCase();
      if (lower.includes("android") || lower.includes("ios") || lower.includes("iphone")) {
        return "mdi-cellphone";
      }
      if (lower.includes("mac")) return "mdi-apple";
      if (lower.includes("windows")) return "mdi-microsoft-windows";
      if (lower.includes("linux")) return "mdi-linux";
      return "mdi-laptop";
    },
    formatLocation(item) {
      const parts = [];
      if (item.city) parts.push(item.city);
      if (item.country) parts.push(item.country);
      return parts.length > 0 ? parts.join(", ") : "Unknown Location";
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return moment(dateStr).format("DD MMM YYYY, h:mm:ss A");
    },
    formatRelativeTime(dateStr) {
      if (!dateStr) return "";
      return moment(dateStr).fromNow();
    },
    copyIp(ip) {
      if (navigator.clipboard && ip) {
        navigator.clipboard.writeText(ip);
        this.$toast?.success ? this.$toast.success(`Copied IP: ${ip}`) : null;
      }
    },
  },
};
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.font-monospace {
  font-family: monospace;
}
</style>
